// ** Modules
import { useState } from 'react';

// ** UI
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// ** Types
import type { DashboardEventsSection } from './types';

// ** Shared
import { DashboardBodyText, DashboardCtaLink, DashboardPanel } from './shared';

// ** Components
import { Icon } from '@/components/common/icons';

type DashboardEventsPanelProps = {
	events: DashboardEventsSection;
	activeEventIndex: number;
	setActiveEventIndex: (updater: (currentIndex: number) => number) => void;
};

const DashboardEventsPanel = ({ events, activeEventIndex, setActiveEventIndex }: DashboardEventsPanelProps) => {
	const theme = useTheme();
	const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
	const eventCount = events.items.length;
	const safeActiveEventIndex = eventCount > 0 ? Math.min(activeEventIndex, eventCount - 1) : 0;
	const [transitionDirection, setTransitionDirection] = useState<'next' | 'prev'>('next');
	const activeEvent = events.items[safeActiveEventIndex];
	const canGoPrev = safeActiveEventIndex > 0;
	const canGoNext = safeActiveEventIndex < eventCount - 1;
	const animatedContentSx = {
		animation: prefersReducedMotion
			? 'none'
			: transitionDirection === 'next'
				? 'dashboardEventSlideNext 280ms cubic-bezier(0.22, 1, 0.36, 1)'
				: 'dashboardEventSlidePrev 280ms cubic-bezier(0.22, 1, 0.36, 1)',
		'@keyframes dashboardEventSlideNext': {
			from: { opacity: 0.42, transform: 'translateX(18px)' },
			to: { opacity: 1, transform: 'translateX(0)' }
		},
		'@keyframes dashboardEventSlidePrev': {
			from: { opacity: 0.42, transform: 'translateX(-18px)' },
			to: { opacity: 1, transform: 'translateX(0)' }
		}
	};

	const arrowButtonSx = {
		p: 0.5,
		borderRadius: 999,
		color: theme.palette.common.black,
		backgroundColor: 'transparent !important',
		transition: 'transform 140ms ease, background-color 140ms ease, box-shadow 140ms ease',
		'&:hover': {
			transform: 'translateY(-1px)',
			color: theme.palette.primary.main,
			backgroundColor: 'transparent !important',
			boxShadow: 'none'
		},
		'&:focusVisible': { backgroundColor: 'transparent !important' },
		'&.Mui-focusVisible': { backgroundColor: 'transparent !important' },
		'& .MuiTouchRipple-root': { display: 'none' },
		'&:active': { transform: 'translateY(0) scale(0.98)' },
		'&.Mui-disabled': {
			color: theme.palette.grey[400],
			opacity: 1
		},
		'&.Mui-disabled:hover': {
			transform: 'none',
			color: theme.palette.grey[400],
			boxShadow: 'none'
		},
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
			'&:hover': { transform: 'none' },
			'&:active': { transform: 'none' }
		}
	};

	const arrowControls =
		eventCount > 1 ? (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					columnGap: 8,
					color: theme.palette.common.black,
					flex: '0 0 auto'
				}}
			>
				<IconButton
					size="small"
					aria-label="Vorherige Veranstaltung"
					onClick={() => {
						setTransitionDirection('prev');
						setActiveEventIndex((currentIndex) => Math.max(currentIndex - 1, 0));
					}}
					disabled={!canGoPrev}
					disableRipple
					sx={arrowButtonSx}
				>
					<Icon name="arrowLeft" opacity={canGoPrev ? 1 : 0.32} />
				</IconButton>
				<IconButton
					size="small"
					aria-label="Naechste Veranstaltung"
					onClick={() => {
						setTransitionDirection('next');
						setActiveEventIndex((currentIndex) => Math.min(currentIndex + 1, eventCount - 1));
					}}
					disabled={!canGoNext}
					disableRipple
					sx={arrowButtonSx}
				>
					<Icon name="arrowRight" opacity={canGoNext ? 1 : 0.32} />
				</IconButton>
			</Box>
		) : null;

	return (
		<DashboardPanel
			title={events.title}
			cta={events.cta}
			sx={{
				pb: { xs: 6 },
				'& > div:first-of-type': {
					mb: { xs: 8, md: 20 }
				},
				// Hide CTA from the panel header on mobile — it moves to the bottom row
				'& > div:first-of-type > div': {
					display: { xs: 'none', md: 'block' }
				}
			}}
		>
			{activeEvent ? (
				<Box
					key={activeEvent.id}
					aria-live="polite"
					aria-atomic="true"
					aria-label={eventCount > 1 ? `Veranstaltung ${safeActiveEventIndex + 1} von ${eventCount}` : undefined}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						flex: '1 1 auto',
						minHeight: 0
					}}
				>
					{/* Headline with left border */}
					<Box
						sx={{
							...animatedContentSx,
							display: 'flex',
							width: '100%',
							maxWidth: '80%',
							flexDirection: 'column',
							justifyContent: 'center',
							pl: 4,
							borderLeft: {
								xs: `4px solid ${theme.palette.grey[300]}`,
								md: `2px solid ${theme.palette.grey[300]}`
							}
						}}
					>
						<DashboardBodyText
							variant="body-lg"
							sx={{
								display: '-webkit-box',
								WebkitLineClamp: 3,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden'
							}}
						>
							{activeEvent.headline}
						</DashboardBodyText>
					</Box>

					{/*
					 * Bottom area — flex-wrap handles both breakpoints without JS:
					 * Mobile:  date spans full row (flex: 0 0 100%), then CTA + arrows
					 *          wrap onto a second row (rowGap: 8 = 32px between rows).
					 * Desktop: date + arrows share one row; CTA is display:none.
					 */}
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: { xs: 'flex-end', md: 'center' },
							justifyContent: 'space-between',
							columnGap: 3,
							rowGap: { xs: 8, md: 0 },
							mt: { xs: 8, md: 15 }
						}}
					>
						{/* Date */}
						<Typography
							variant="display-md"
							component="p"
							sx={{
								...animatedContentSx,
								flex: { xs: '0 0 100%', md: '0 0 auto' },
								m: 0,
								color: theme.palette.primary.main
							}}
						>
							{activeEvent.date}
						</Typography>

						{/* CTA — mobile only, bottom-left next to arrows */}
						{events.cta ? (
							<Box
								sx={{
									display: { xs: 'flex', md: 'none' },
									flex: '1 1 auto',
									alignItems: 'center'
								}}
							>
								<DashboardCtaLink cta={events.cta} />
							</Box>
						) : null}

						{/* Arrow controls */}
						{arrowControls}
					</Box>
				</Box>
			) : (
				<DashboardBodyText>{events.emptyText ?? 'Keine Veranstaltung hinterlegt'}</DashboardBodyText>
			)}
		</DashboardPanel>
	);
};

export default DashboardEventsPanel;
